import React from 'react';
import { DailySummaryData } from '../DailyNews/DailySummary';
import './DailySummaryCard.css';

export interface DailySummaryCardProps {
  dailySummary: DailySummaryData;
  showStats?: boolean;
  onClick?: () => void;
  className?: string;
}

const DailySummaryCard: React.FC<DailySummaryCardProps> = ({
  dailySummary,
  showStats = true,
  onClick,
  className = '',
}) => {
  const formatDayOfWeek = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  // Truncate summary for card view
  const truncateSummary = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div 
      className={`daily-summary-card ${dailySummary.featured ? 'daily-summary-card--featured' : ''} ${onClick ? 'daily-summary-card--clickable' : ''} ${className}`}
      onClick={handleClick}
    >
      {/* Header */}
      <header className="daily-summary-card__header">
        <time 
          className="daily-summary-card__date"
          dateTime={dailySummary.date}
          title={formatDayOfWeek(dailySummary.date)}
        >
          {formatDayOfWeek(dailySummary.date)}
        </time>
        {dailySummary.featured && (
          <span className="daily-summary-card__featured-badge">
            Featured
          </span>
        )}
      </header>

      {/* Title */}
      <h3 className="daily-summary-card__title">
        {dailySummary.title}
      </h3>

      {/* Summary */}
      <div className="daily-summary-card__content">
        <p className="daily-summary-card__text">
          {truncateSummary(dailySummary.summary)}
        </p>
      </div>

      {/* Key Themes (limited) */}
      {(() => {
        const keyThemes: string[] = typeof dailySummary.key_themes === 'string' 
          ? JSON.parse(dailySummary.key_themes || '[]') 
          : dailySummary.key_themes || [];
        
        return keyThemes && keyThemes.length > 0 && (
          <div className="daily-summary-card__themes">
            {keyThemes.slice(0, 3).map((theme: string, index: number) => (
              <span key={index} className="daily-summary-card__theme">
                {theme}
              </span>
            ))}
            {keyThemes.length > 3 && (
              <span className="daily-summary-card__theme daily-summary-card__theme--more">
                +{keyThemes.length - 3}
              </span>
            )}
          </div>
        );
      })()}

      {/* Stats */}
      {showStats && (
        <footer className="daily-summary-card__footer">
          <div className="daily-summary-card__stats">
            <span className="daily-summary-card__stat" title="Articles">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              {dailySummary.article_count}
            </span>

            {dailySummary.view_count > 0 && (
              <span className="daily-summary-card__stat" title="Views">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {dailySummary.view_count}
              </span>
            )}
          </div>

          {onClick && (
            <div className="daily-summary-card__click-hint">
              Click to view day
            </div>
          )}
        </footer>
      )}
    </div>
  );
};

export default DailySummaryCard;
